import { useEffect, useId, useState, type ReactNode } from 'react';
import { EnvelopeStatusBadge } from '@/components/docusign/EnvelopeStatusBadge';
import { ApiError, api } from '@/lib/api';
import {
  docuSignEnvironmentLabel,
  signatureConsequenceNote,
  signaturesNotBinding,
  signerSummary,
  signerCount,
} from '@/lib/docusign';
import { formatDateTime, pluralise } from '@/lib/format';
import type { DocuSignConnection, DocuSignEnvelope } from '@/lib/types';
import {
  Badge,
  Button,
  EmptyState,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';

/*
 * DocuSign, on the Integrations page, beside Xero. A sibling panel by design —
 * the page is a list of integrations, not "the Xero screen with an extra bit".
 *
 * This card reads TWO things:
 *
 * 1. `GET /docusign/connection` — is it configured, consented, and which
 *    environment; and
 * 2. `GET /docusign/envelopes?outstanding=true` — how many signature requests
 *    are still out, and a way through to each.
 *
 * **They are two independent reads and neither gates the other.** The
 * connection is ADMIN-ONLY and the envelope list is readable by any
 * authenticated role, so a non-admin sees a 403 explanation above a working
 * list — which is the correct pair of facts, not a broken screen. Equally, an
 * envelope sent last week is still outstanding whether or not the connection
 * reads cleanly today, so the list is never suppressed on a connection failure.
 *
 * Nothing here talks to DocuSign. Both calls go to our own API, which holds the
 * RSA key and performs the JWT grant; see `@/lib/docusign`.
 *
 * ⚠️ **There is NO CONNECT and NO DISCONNECT control, and one must not be
 * added.** DocuSign is a JWT Bearer grant: the backend signs an assertion with a
 * private RSA key and exchanges it for a token. There is no handshake to start,
 * no callback to return to, no connection row and **no token to revoke** — so
 * there is nothing for a button to do. Xero is the opposite and genuinely needs
 * `POST /xero/connect` to mint a `state` nonce before the browser may leave; do
 * not copy that panel's shape across. The one link this card may offer is
 * DocuSign's own `consentUrl`, which is static and is simply opened in a new tab.
 *
 * ⚠️ **THERE IS NO WEBHOOK.** DocuSign tells us nothing; a status moves only
 * when something calls `POST /docusign/envelopes/{id}/refresh`. So every status
 * here is a LAST KNOWN status and is dated as one — `statusCheckedAt`, never
 * `updatedAt`. Nothing on this card may imply live monitoring.
 *
 * ⚠️ **"Something" is doing work there — corrected 2026-08-30.** A backend
 * worker calls that route on a timer for outstanding envelopes, so this card's
 * figures can move with nobody on it; see `BACKGROUND_CHECK_NOTE` in
 * `@/lib/docusign` for the verified interval and the three caveats that stop it
 * being a promise. The rule above is UNCHANGED — it is still a last known
 * status, still dated, still not monitoring — but its reason is now "the pass is
 * periodic and not guaranteed", not "only a person can ever ask".
 */

interface ConnectionState {
  data: DocuSignConnection | null;
  loading: boolean;
  error: string | null;
  /** The read is admin-only. A 403 is a role, not a fault, and reads as one. */
  forbidden: boolean;
}

const IDLE: ConnectionState = { data: null, loading: true, error: null, forbidden: false };

/**
 * How many outstanding envelopes to name on the card.
 *
 * A summary, not a list screen — the card's job is "how many are out, and how do
 * I get to one". `meta.total` carries the real figure regardless, so the cap
 * never hides the number, only the rows.
 */
const SHOWN = 4;

/** The outstanding-envelope read, and whatever it last failed with. */
interface EnvelopeState {
  /** The newest {@link SHOWN}. */
  rows: DocuSignEnvelope[];
  /**
   * `meta.total` — every outstanding envelope, not `rows.length`. The two differ
   * the moment there are more than {@link SHOWN} and a badge built from the
   * array would quietly under-report exactly when the number starts mattering.
   */
  total: number;
  loading: boolean;
  /**
   * ⚠️ **A sibling of `rows`, never a replacement for it.** A re-read can fail
   * while the previous list is still on screen and still true, and both facts
   * need showing — `error ?? rows` is the silent drop this repo keeps finding.
   */
  error: string | null;
  /** The backend has not mounted its DocuSign half. A calm state, not a red one. */
  notWired: boolean;
}

const IDLE_ENVELOPES: EnvelopeState = {
  rows: [],
  total: 0,
  loading: true,
  error: null,
  notWired: false,
};

/** The empty-state glyph: a pen, mid-signature. */
function SignatureIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M3 19c3.5 0 3.5-11 6.5-11S13 17 16 17c1.6 0 2.5-1 3-2" />
      <path d="M3 21h18" />
    </svg>
  );
}

/** One labelled fact. Mirrors the Xero panel's; pairs, not rows. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

/** A failure worth someone's attention. `anywhere` because an API message can carry an id. */
function ErrorBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </Notice>
  );
}

/**
 * The heading on the not-binding warning. Three cases, because they are three
 * different pieces of news and the loudest line on the card should say which.
 *
 * The middle one is the point of keeping `baseUri` in the rule at all: it comes
 * from DocuSign's `/oauth/userinfo`, so `environment: 'production'` sitting
 * beside a `demo` host is our own config disagreeing with DocuSign, and that is
 * worth surfacing rather than resolving silently in either direction.
 */
function notBindingHeading(connection: DocuSignConnection | null): string {
  if (!connection) return 'Unconfirmed — treat signatures as not legally binding';
  if (connection.environment === 'production') {
    return 'Environment mismatch — signatures may not be legally binding';
  }
  return 'Demo — signatures are not legally binding';
}

/**
 * The warning, and it is meant to be the loudest thing on this card.
 *
 * ⚠️ **CORRECTED 2026-09-01. DocuSign moved to PRODUCTION on that date** —
 * account SEED Global Education, host na4, verified against the userinfo
 * endpoint. *SUPERSEDED: "The server credentials are the DocuSign sandbox,
 * deliberately."* Signatures sent from here are now legally binding, and the
 * warning below no longer fires on this deployment.
 *
 * **The warning stays anyway, and the reasoning below is why it must.** It is
 * keyed on what the server REPORTS, never on a hardcoded environment, so it
 * arms itself again the moment anyone points DocuSign back at a sandbox. What
 * follows describes that state, which is no longer today's:
 *
 * Where the server credentials are a sandbox, production
 * therefore reports DocuSign as connected and working while every envelope it
 * would send is worthless — someone can send a customer a signing link, watch
 * it come back "signed", and have nothing. **The failure mode looks exactly
 * like success**, which is why this is a block with sentences in it rather than
 * a chip, and why it renders in EVERY non-loading state including the error one.
 *
 * When real credentials land, `environment` flips to `production` on its own,
 * the account host stops containing `demo`, and this disappears with no code
 * change here or anywhere else.
 */
function NotBindingWarning({ connection }: { connection: DocuSignConnection | null }) {
  return (
    <Notice
      tone="warning"
      role="status"
      title={
        <span className="text-sm [overflow-wrap:anywhere]">{notBindingHeading(connection)}</span>
      }
    >
      <div className="min-w-0">
        <p className="m-0 text-xs [overflow-wrap:anywhere]">
          {connection
            ? 'Sandbox envelopes — a completed signature is not enforceable.'
            : 'The connection could not be read, so assume sandbox.'}
        </p>
        <p className="m-0 mt-1 text-xs [overflow-wrap:anywhere]">
          Environment: {docuSignEnvironmentLabel(connection?.environment)} &middot; Host:{' '}
          {connection?.account?.baseUri ?? 'not reported'}
        </p>
      </div>
    </Notice>
  );
}

/**
 * What to call the quote an envelope settles.
 *
 * Both `quoteNumber` and `subject` are nullable on the join, and the row id is
 * the one thing always present — so the fallback is a real reference someone can
 * act on rather than an em dash. A signature request with no name on it is
 * indistinguishable from a rendering fault.
 */
function quoteLabel(quote: DocuSignEnvelope['quote']): string {
  return quote.quoteNumber ?? quote.subject ?? `Quote #${quote.id}`;
}

/**
 * One outstanding signature request.
 *
 * **The way through is the quote, not an envelope screen** — there is no
 * `/envelopes` route and there is not meant to be. An envelope settles exactly
 * one quote, and everything a person can DO with it (refresh it, mint a signing
 * link, void it) lives on that quote's own panel. Linking anywhere else would be
 * a link to a page that cannot act.
 *
 * The status is dated with `statusCheckedAt` and never with `updatedAt`: there is
 * no webhook, so "we asked two minutes ago and it is still out" and "nothing has
 * asked since Tuesday" are different facts and the second one must not be able
 * to read as the first. (The second is rarer now that a worker asks on a timer,
 * and it is still reachable — the pass backs off after a failure and does not
 * run where DocuSign is unconfigured — which is exactly why the date stays.)
 */
function EnvelopeRow({
  envelope,
  connection,
}: {
  envelope: DocuSignEnvelope;
  connection: DocuSignConnection | null;
}) {
  return (
    <li className="flex min-w-0 flex-col gap-1.5 border-t border-border py-2.5 first:border-t-0 first:pt-0">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <TextLink
          to={`/quotes/${envelope.quote.id}`}
          className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]"
        >
          {quoteLabel(envelope.quote)}
        </TextLink>
        {/*
         * The ONE copy of status presentation, consumed rather than restated.
         * The Xero cards learned this the expensive way: literal status words on
         * one screen drifted from `statusLabel` on the next, and a literal string
         * cannot go stale noisily. It is also where the not-legally-binding
         * qualification is applied to the WORD, which is why the connection is
         * threaded through rather than left to default.
         */}
        <EnvelopeStatusBadge
          status={envelope.status}
          connection={connection}
          terminal={envelope.terminal}
          statusCheckedAt={envelope.statusCheckedAt}
        />
      </div>
      <p className="m-0 min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
        {/* Names every signer, not only the first — an envelope may ask up to four
            people and "Sent to Ada" on a three-signer request is a quiet undercount. */}
        Sent to {signerSummary(envelope)}
        {signerCount(envelope) === 1 && envelope.signer.email ? (
          <>
            {' '}
            &middot; <EmailLink address={envelope.signer.email} tone="muted" />
          </>
        ) : null}
      </p>
      <p className="m-0 text-xs text-fg-subtle">
        {/*
         * Never a bare dash for a check that has not happened. "Never checked"
         * is a real and actionable state — the envelope may have been signed
         * hours ago and nothing here would know.
         */}
        {envelope.statusCheckedAt
          ? `Last checked with DocuSign ${formatDateTime(envelope.statusCheckedAt)}`
          : 'Never checked with DocuSign'}
      </p>
    </li>
  );
}

/** Skeletons in the real shape of {@link EnvelopeRow}, not a bare spinner. */
function EnvelopeRowsSkeleton() {
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {[0, 1].map((key) => (
        <li
          key={key}
          className="flex flex-col gap-1.5 border-t border-border py-2.5 first:border-t-0 first:pt-0"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-40" />
        </li>
      ))}
    </ul>
  );
}

export function DocuSignCard({
  canManage,
  manageDenial,
}: {
  /**
   * Same gate the Xero panel uses — `ability.canUpdate('integrations')`, from
   * the page. ⚠️ *SUPERSEDED 2026-09-02: `ability.canUpdate('companies')`*, the
   * borrowed slug that handed connect and disconnect to everyone allowed to edit
   * an account. The gate is the page's single `INTEGRATION_MODULE` constant, so
   * this card and the Xero panel cannot come to different answers.
   */
  canManage: boolean;
  /** The true reason the control is disabled, rendered as text as well as a title. */
  manageDenial: string | null;
}) {
  const [state, setState] = useState<ConnectionState>(IDLE);
  const [envelopes, setEnvelopes] = useState<EnvelopeState>(IDLE_ENVELOPES);
  const headingId = useId();
  const outstandingHeadingId = useId();

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .docusignConnection(signal)
      .then(({ data }) => {
        if (signal.aborted) return;
        setState({ data, loading: false, error: null, forbidden: false });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * 401 is left entirely to `api.ts`, which already attempts one refresh
         * and then ends the session — a message here would contradict it
         * midway through signing out. 403 is the role, not a failure. 404 means
         * the backend has not mounted its DocuSign half yet, which is a calm
         * state rather than a red one.
         */
        if (caught instanceof ApiError && caught.status === 401) return;
        if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) {
          setState({
            data: null,
            loading: false,
            error: null,
            forbidden: caught.status === 403,
          });
          return;
        }
        setState({
          data: null,
          loading: false,
          error:
            caught instanceof Error ? caught.message : 'Could not read the DocuSign connection.',
          forbidden: false,
        });
      })
      .finally(() => {
        if (signal.aborted) return;
        setState((current) => ({ ...current, loading: false }));
      });

    return () => controller.abort();
  }, []);

  /*
   * The outstanding envelopes, read INDEPENDENTLY of the connection above.
   *
   * Deliberately not chained behind it, for two reasons that pull the same way.
   * `GET /docusign/envelopes` is readable by ANY authenticated role while the
   * connection is admin-only, so gating on the connection would blank a list a
   * basic user is entitled to see, on the strength of a 403 that says nothing
   * about the envelopes. And an envelope sent last week is outstanding whether
   * or not the connection reads cleanly today — suppressing the list on a
   * connection failure would report "nothing is out" at the moment least
   * qualified to say so.
   */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      /*
       * `outstanding: true` is the server's own definition — not completed,
       * declined or voided — rather than a client-side filter over a status
       * list. `TERMINAL_STATUSES` is mirrored in `@/lib/docusign` precisely
       * because nothing makes the two agree automatically, so the filter that
       * needs no mirror is the one to use.
       *
       * `order` is not sent: the server orders newest first and this card
       * renders what it is given.
       */
      .listDocusignEnvelopes({ outstanding: true, perPage: SHOWN }, signal)
      .then(({ data, meta }) => {
        if (signal.aborted) return;
        setEnvelopes({
          rows: data,
          // From `meta.total`, never `data.length` — see the field's own
          // doc comment on `EnvelopeState`.
          total: meta.total,
          loading: false,
          error: null,
          notWired: false,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /* Same split as the connection read above, and for the same reasons. */
        if (caught instanceof ApiError && caught.status === 401) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setEnvelopes({ rows: [], total: 0, loading: false, error: null, notWired: true });
          return;
        }
        setEnvelopes((current) => ({
          /*
           * The previous rows are KEPT. They are still the last thing the
           * server actually said, and replacing a true answer with no answer
           * is worse than showing both.
           */
          ...current,
          loading: false,
          error:
            caught instanceof Error
              ? caught.message
              : 'Could not read the outstanding signature requests.',
          notWired: false,
        }));
      })
      .finally(() => {
        if (signal.aborted) return;
        setEnvelopes((current) => ({ ...current, loading: false }));
      });

    return () => controller.abort();
  }, []);

  const { data: connection, loading, error, forbidden } = state;
  const notBinding = signaturesNotBinding(connection);
  const consentUrl = connection?.consentUrl ?? null;

  function grantConsent() {
    if (!consentUrl) return;
    /*
     * Straight to DocuSign in a new tab. No XHR first and no `state` nonce to
     * mint — the URL the server already handed us is the whole flow. A new tab
     * rather than a navigation because there is nothing to come back to: the
     * consent lands server-side, and this page is re-read on the next visit.
     */
    window.open(consentUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        title="DocuSign"
      />
      <PanelBody className="flex flex-col gap-3">
        {/*
         * Above everything, and in every state but loading. Fail safe: no
         * response means no `environment`, and that is the moment we know
         * least — see `signaturesNotBinding()`.
         */}
        {!loading && notBinding && <NotBindingWarning connection={connection} />}

        {error && <ErrorBlock title="Couldn’t read the DocuSign connection.">{error}</ErrorBlock>}

        {/*
         * Rendered alongside whichever state holds rather than instead of it —
         * `problem` names the likely cause and is the server's own words, so it
         * goes out verbatim.
         */}
        {connection?.problem && (
          <Notice tone="warning" role="status">
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{connection.problem}</p>
          </Notice>
        )}

        {loading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-56" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
        ) : forbidden ? (
          <EmptyState
            icon={<SignatureIcon />}
            title="Only administrators can read this connection"
            description="Admin accounts only. Nothing else is affected."
          />
        ) : !connection ? (
          <EmptyState
            icon={<SignatureIcon />}
            title="DocuSign is not set up on the API yet"
            description="The API has no DocuSign endpoint to answer this yet."
          />
        ) : connection.consented ? (
          <>
            {/*
             * The account name is the loudest fact here for the same reason the
             * Xero tenant name is: one login can reach more than one account,
             * and sending a customer document from the wrong one is not
             * something this app can detect or undo. Never left implicit.
             */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <p className="m-0 min-w-0 text-lg font-semibold text-fg [overflow-wrap:anywhere]">
                {connection.account?.name ?? '—'}
              </p>
              <Badge tone="success" dot>
                Connected
              </Badge>
              {/*
               * The success badge must never stand alone. On its own it reads
               * as "we are live", which is precisely the wrong conclusion while
               * the credentials are the sandbox.
               */}
              {notBinding && (
                <Badge tone="warning" dot>
                  Not legally binding
                </Badge>
              )}
            </div>

            <dl className="grid gap-3 sm:grid-cols-2">
              {/*
               * ⛔ **TWO ROWS WERE REMOVED HERE — owner's ruling, 2026-09-01,
               * and this comment is the second correction to the same pair in
               * two days. Both are quoted so neither reads as an oversight.**
               *
               * *SUPERSEDED, 2026-08-31, the first pass:* `<Fact label="Signed
               * in as">{connection.account?.userName ?? '—'}</Fact>` and
               * `<Fact label="Account email">{connection.account?.userEmail ??
               * '—'}</Fact>`, on the instruction *"why is it saying Paras Global
               * Fatnani — replace this with SEED Global Education"*. That pass
               * repointed `Signed in as` at `account.name` and left the email
               * alone.
               *
               * *SUPERSEDED, 2026-08-31, the second:* `<Fact label="Signed in
               * as">{connection.account?.name ?? '—'}</Fact>`, kept alongside
               * the email row, with a note flagging that it now read identically
               * to the heading above and that the person was still named one row
               * below.
               *
               * **Owner's ruling on that flag: remove both.** The heading already
               * renders `account.name` with a Connected badge, so `Signed in as`
               * was pure repetition — and `Account email` was still `userEmail`,
               * the impersonated USER's address, which is the individual he
               * objected to being named at all. Dropping the pair says the same
               * thing once and names nobody.
               *
               * ⚠️ **WHICH ACCOUNT IS CONNECTED IS STILL PROVEN, and that was
               * checked rather than assumed.** The heading above is a separate
               * render of `connection.account?.name`, so the loudest fact on
               * this card is untouched — the reason it is loud is unchanged and
               * still stated there: one login can reach more than one account,
               * and sending a customer document from the wrong one is not
               * something this app can detect or undo.
               *
               * ⚠️ **`userName` and `userEmail` STAY on `DocuSignAccount` and on
               * the wire.** They are the server's response shape, not ours, and
               * `/oauth/userinfo` keeps returning them. Measured before deleting
               * these rows: `userName` had NO other consumer in `src/`, and
               * `userEmail`'s only one was the row above. So nothing else went
               * dark — they simply stop being rendered anywhere.
               */}
              <Fact label="Environment">{docuSignEnvironmentLabel(connection.environment)}</Fact>
              <Fact label="Account host">
                {connection.account?.baseUri ? (
                  <span className="font-mono text-xs">{connection.account.baseUri}</span>
                ) : (
                  '—'
                )}
              </Fact>
            </dl>
          </>
        ) : !connection.configured ? (
          /*
           * Not the same as "not connected", and the difference decides whether
           * there is anything to offer. `configured: false` means the SERVER
           * holds no DocuSign credentials — nobody signed in here can fix that,
           * so a button would only produce an error. **No button.**
           */
          <EmptyState
            icon={<SignatureIcon />}
            title="DocuSign is not configured on the server"
            description="No DocuSign credentials on the server. Set them in the server’s env file."
          />
        ) : (
          <EmptyState
            icon={<SignatureIcon />}
            title="DocuSign is not connected"
            description="Consent is given once, in DocuSign’s own window. Nothing is sent by granting it."
            action={
              <div className="flex flex-col items-center gap-2">
                <Button
                  variant="primary"
                  disabled={!canManage || !consentUrl}
                  title={manageDenial ?? undefined}
                  onClick={grantConsent}
                >
                  Grant consent in DocuSign
                </Button>
                {!consentUrl && (
                  <p className="m-0 text-xs text-fg-subtle">
                    No consent link reported, so there is nothing to open.
                  </p>
                )}
                {/*
                 * The reason is text as well as a `title`: a disabled button has
                 * `pointer-events: none`, so its tooltip never reaches the person
                 * it was written for.
                 */}
                {manageDenial && <p className="m-0 text-xs text-fg-subtle">{manageDenial}</p>}
              </div>
            }
          />
        )}

        {/* ── Outstanding signature requests ─────────────────────────────── */}

        <section
          aria-labelledby={outstandingHeadingId}
          className="flex flex-col gap-2.5 border-t border-border pt-3"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h3
              id={outstandingHeadingId}
              className="m-0 min-w-0 text-sm font-semibold text-fg [overflow-wrap:anywhere]"
            >
              Out for signature
            </h3>
            {/*
             * The count is a second signal beside the rows, and it is only
             * rendered once the read has settled — a badge over a list that is
             * still loading asserts a figure nobody has yet been told.
             */}
            {!envelopes.loading && !envelopes.notWired && envelopes.total > 0 && (
              <Badge tone="neutral" variant="outline">
                {pluralise(envelopes.total, 'quote')} awaiting signature
              </Badge>
            )}
          </div>

          {/*
           * ⚠️ **What this integration DOES, not merely that it is connected.**
           *
           * A completed signature is not a status change somebody chose: the
           * refresh that observes it moves the quote to the accepted stage —
           * with nobody asked to confirm, because the customer signing IS the
           * confirmation and there is no human at a screen. Acceptance moves
           * the stage and nothing more — no invoice, no Xero push.
           *
           * `signatureConsequenceNote()` in `@/lib/docusign` is the ONE copy,
           * and it is a function rather than two adjacent strings on purpose:
           * the sandbox caveat and the consequence are BOTH true at once, and
           * rendering only one of them is this repo's recurring `a ?? b` defect.
           */}
          <p className="m-0 max-w-2xl text-xs text-fg-muted [overflow-wrap:anywhere]">
            {signatureConsequenceNote(connection)}
          </p>

          {/*
           * Rendered ALONGSIDE whatever rows survive, never instead of them —
           * a failed re-read does not make the last answer untrue.
           */}
          {envelopes.error && (
            <ErrorBlock title="Couldn’t read the outstanding signature requests.">
              {envelopes.error}
            </ErrorBlock>
          )}

          {envelopes.loading && envelopes.rows.length === 0 ? (
            <EnvelopeRowsSkeleton />
          ) : envelopes.notWired ? (
            <EmptyState
              icon={<SignatureIcon />}
              title="The API has no signature requests yet"
              description="This list appears once the backend ships its DocuSign endpoints."
            />
          ) : envelopes.rows.length === 0 ? (
            <EmptyState
              icon={<SignatureIcon />}
              title="Nothing is out for signature"
              description="No quote is waiting on a signature. Requests are sent from a quote."
            />
          ) : (
            <>
              <ul className="m-0 flex list-none flex-col p-0">
                {envelopes.rows.map((envelope) => (
                  <EnvelopeRow key={envelope.id} envelope={envelope} connection={connection} />
                ))}
              </ul>

              {/*
               * ⛔ **TWO SENTENCES WERE REMOVED HERE — owner's helper-text
               * ruling, 2026-09-05. Both are quoted so neither reads as an
               * oversight.**
               *
               * *SUPERSEDED, the truncation line:* `{N} more requests not shown
               * — each is on its own quote.` It existed because there is no
               * envelopes index to link to, so a "view all" would have pointed
               * at a route that does not exist. **The list is still truncated
               * and now says nothing about it.**
               *
               * *SUPERSEDED, the liveness line:* `STATUS_IS_LAST_KNOWN_NOTE`.
               *
               * ⚠️ **THERE IS STILL NO WEBHOOK AND NOTHING HERE IS LIVE.** The
               * constant is untouched in `@/lib/docusign` and the standing rule
               * is unchanged: **never render a DocuSign status as live, and
               * never write copy implying a signature takes effect on its own.**
               * What went is the page repeating it, not the rule — so a future
               * status column here must still be worded as *last checked*, never
               * as *status as of*.
               */}
            </>
          )}
        </section>
      </PanelBody>
    </Panel>
  );
}
